/* eslint-disable @typescript-eslint/no-explicit-any */
import { RcFile } from 'antd/lib/upload';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { auth } from '../state/firebase';

let cancelTokenSource: CancelTokenSource;

const axiosApiInstance = axios.create();
export default async function createApiInstance(accessToken: string, baseUrl: string) {
  axiosApiInstance.interceptors.request.use(
    async config => {
      const context = '';
      config.baseURL = `${baseUrl}${context}`;
      return config;
    },
    error => {
      Promise.reject(error);
    }
  );
}

export function handleRequestErrors(error: AxiosError) {
  const { response } = error as AxiosError;
  const statusError = response?.status;
  if (statusError === 403) window.open('/unauthorized', '_self');
}

export const getToken = async (): Promise<string> => {
  const user = auth.currentUser;
  let token = '';
  if (user) token = await user.getIdToken();
  return `Bearer ${token}`;
};

export async function apiGet<T, U>(url: string, options?: T): Promise<U> {
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    const { data } = await axiosApiInstance.get<U>(url, { ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiGetWithCancelation<T, U>(url: string, options?: T): Promise<U> {
  if (cancelTokenSource) {
    cancelTokenSource.cancel('Solicitud cancelada');
  }
  cancelTokenSource = axios.CancelToken.source();
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    const { data } = await axiosApiInstance.get<U>(url, { ...options, headers, cancelToken: cancelTokenSource?.token });
    return data;
  } catch (error) {
    handleRequestErrors(error as AxiosError);
    throw error;
  }
}

export async function apiPut<T, U = any, V = any>(url: string, body?: U, options?: T): Promise<V> {
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    const { data }: any = await axiosApiInstance.put(url, body, { ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiPost<T, U = any, V = any>(url: string, body?: U, options?: T): Promise<V> {
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    return await axiosApiInstance.post(url, body, { ...options, headers });
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiDelete<T, U = any, V = any>(url: string, body?: U, options?: T): Promise<V> {
  try {
    const token = await getToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token
    };
    const { data }: any = await axiosApiInstance.delete(url, { data: body, ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiPostFile<T, V = any>(
  url: string,
  fileData: string | Blob | RcFile | FormData,
  options?: T,
  destructuringData?: boolean
): Promise<V> {
  try {
    const isFormData = fileData instanceof FormData;

    const token = await getToken();
    var formData = new FormData();
    if (!isFormData) formData.append('file', fileData);

    const file = isFormData ? fileData : formData;

    const headers = {
      'Content-Type': 'multipart/form-data',
      Authorization: token
    };
    if (destructuringData) {
      const { data } = await axiosApiInstance.post(url, file, { ...options, headers });
      return data;
    }
    return await axiosApiInstance.post(url, file, { ...options, headers });
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiPatch<T = any, U = any, V = any>(url: string, body?: U, options?: T): Promise<V> {
  const token = await getToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: token
  };
  try {
    const { data }: any = await axiosApiInstance.patch(url, body, { ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}

export async function apiPutFile<T, U = any, V = any>(url: string, body?: FormData, options?: T): Promise<V> {
  const token = await getToken();
  const headers = {
    'Content-Type': 'multipart/form-data',
    Authorization: token
  };
  try {
    const { data }: any = await axiosApiInstance.put(url, body, { ...options, headers });
    return data;
  } catch (err) {
    handleRequestErrors(err as AxiosError);
    throw err;
  }
}
